import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Settings, Store, Plus, MonitorSmartphone, Star, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-200 text-slate-600",
  closed: "bg-rose-100 text-rose-700",
};

function ScannerSoundSetting() {
  const [on, setOn] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("epos_scan_sound") !== "0" : true));
  const toggle = () => setOn((s) => { const n = !s; try { localStorage.setItem("epos_scan_sound", n ? "1" : "0"); } catch (e) {} return n; });
  return (
    <section>
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-3"><Settings className="w-5 h-5 text-slate-500" /> Scanner</h2>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="epos-scanner-sound-setting">
        <div>
          <div className="font-medium text-slate-800">Scanner sound</div>
          <div className="text-xs text-slate-500">Play a beep on each scan. Visual feedback stays on regardless. Saved on this device.</div>
        </div>
        <button onClick={toggle} data-testid="epos-scanner-sound-toggle"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-indigo-600" : "bg-slate-300"}`}
          aria-pressed={on} aria-label="Scanner sound">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
        </button>
      </div>
    </section>
  );
}

export default function EposSettings() {
  const [stores, setStores] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStore, setNewStore] = useState("");
  const [newReg, setNewReg] = useState({ store_id: "", name: "" });
  const [quickKeys, setQuickKeys] = useState([]);
  const [newQk, setNewQk] = useState({ title: "", sku: "", price: "", register_id: "" });

  const loadQuickKeys = useCallback(async () => {
    try {
      // fetch company-wide + each register's keys, dedupe by id
      const regs = (await api.get("/epos/registers")).data.items || [];
      const calls = [api.get("/epos/quick-keys")].concat(
        regs.map((r) => api.get("/epos/quick-keys", { params: { register_id: r.id } })));
      const res = await Promise.all(calls);
      const map = {};
      res.forEach((rr) => (rr.data.items || []).forEach((k) => { map[k.id] = k; }));
      setQuickKeys(Object.values(map));
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([api.get("/epos/stores"), api.get("/epos/registers")]);
      setStores(s.data.items || []);
      setRegisters(r.data.items || []);
      await loadQuickKeys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally { setLoading(false); }
  }, [loadQuickKeys]);

  useEffect(() => { load(); }, [load]);

  const addStore = async () => {
    if (!newStore.trim()) return;
    try {
      await api.post("/epos/stores", { name: newStore.trim() });
      setNewStore("");
      toast.success("Store created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addRegister = async () => {
    if (!newReg.store_id || !newReg.name.trim()) { toast.error("Pick a store and enter a name"); return; }
    try {
      await api.post("/epos/registers", { store_id: newReg.store_id, name: newReg.name.trim() });
      setNewReg({ store_id: "", name: "" });
      toast.success("Register created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setRegStatus = async (reg, status) => {
    try {
      await api.patch(`/epos/registers/${reg.id}`, { status });
      toast.success(`Register ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const storeName = (sid) => stores.find((s) => s.id === sid)?.name || "—";

  const addQuickKey = async () => {
    if (!newQk.title.trim()) { toast.error("Enter a title"); return; }
    try {
      await api.post("/epos/quick-keys", {
        title: newQk.title.trim(), sku: newQk.sku.trim() || null,
        price: newQk.price === "" ? null : Number(newQk.price),
        register_id: newQk.register_id || null,
      });
      setNewQk({ title: "", sku: "", price: "", register_id: "" });
      toast.success("Quick key added");
      loadQuickKeys();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeQuickKey = async (id) => {
    try {
      await api.delete(`/epos/quick-keys/${id}`);
      toast.success("Removed");
      loadQuickKeys();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="w-full p-6 lg:p-8" data-testid="epos-settings">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
        <Settings className="w-7 h-7 text-indigo-600" /> EPOS Settings
      </h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Manage stores and registers for your company.</p>

      {loading ? <div className="text-slate-400 text-sm py-12 text-center">Loading…</div> : (
        <div className="space-y-8">
          {/* Scanner preferences */}
          <ScannerSoundSetting />
          {/* Stores */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-3"><Store className="w-5 h-5 text-slate-500" /> Stores</h2>
            <div className="space-y-2 mb-3">
              {stores.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`epos-store-row-${s.id}`}>
                  <div><span className="font-medium text-slate-800">{s.name}</span>{s.code && <span className="ml-2 text-xs text-slate-400">{s.code}</span>}</div>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded uppercase ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{s.active ? "active" : "inactive"}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newStore} onChange={(e) => setNewStore(e.target.value)} placeholder="New store name"
                data-testid="epos-new-store-input"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              <button onClick={addStore} data-testid="epos-add-store-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </section>

          {/* Registers */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-3"><MonitorSmartphone className="w-5 h-5 text-slate-500" /> Registers</h2>
            <div className="space-y-2 mb-3">
              {registers.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`epos-register-row-${r.id}`}>
                  <div>
                    <span className="font-medium text-slate-800">{r.name}</span>
                    <span className="ml-2 text-xs text-slate-400">{storeName(r.store_id)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded uppercase ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                    <select value={r.status} onChange={(e) => setRegStatus(r, e.target.value)}
                      data-testid={`epos-register-status-${r.id}`}
                      className="text-xs border border-slate-200 rounded px-2 py-1">
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="closed">closed</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select value={newReg.store_id} onChange={(e) => setNewReg((v) => ({ ...v, store_id: e.target.value }))}
                data-testid="epos-new-register-store"
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Select store…</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input value={newReg.name} onChange={(e) => setNewReg((v) => ({ ...v, name: e.target.value }))}
                placeholder="New register name" data-testid="epos-new-register-input"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              <button onClick={addRegister} data-testid="epos-add-register-btn"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </section>

          {/* Quick keys */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-1"><Star className="w-5 h-5 text-amber-500" /> Quick keys</h2>
            <p className="text-sm text-slate-500 mb-3">Frequently sold products pinned to the register. Company-wide keys show on every register; register-specific keys show only on that register.</p>
            <div className="space-y-2 mb-3" data-testid="epos-quick-keys-list">
              {quickKeys.length === 0 ? (
                <div className="text-slate-400 text-sm rounded-lg border border-dashed border-slate-200 py-6 text-center">No quick keys yet.</div>
              ) : quickKeys.map((k) => (
                <div key={k.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`epos-qk-row-${k.id}`}>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" />
                    <span className="font-medium text-slate-800">{k.title}</span>
                    {k.sku && <span className="text-xs text-slate-400">{k.sku}</span>}
                    {k.price != null && <span className="text-xs text-indigo-600 font-semibold">£{Number(k.price).toFixed(2)}</span>}
                    <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">{k.register_id ? (storeName(registers.find((r) => r.id === k.register_id)?.store_id) + " · " + (registers.find((r) => r.id === k.register_id)?.name || "register")) : "Company-wide"}</span>
                  </div>
                  <button onClick={() => removeQuickKey(k.id)} data-testid={`epos-qk-delete-${k.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={newQk.title} onChange={(e) => setNewQk((v) => ({ ...v, title: e.target.value }))}
                placeholder="Title" data-testid="epos-new-qk-title"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              <input value={newQk.sku} onChange={(e) => setNewQk((v) => ({ ...v, sku: e.target.value }))}
                placeholder="SKU (optional)" data-testid="epos-new-qk-sku"
                className="w-full sm:w-32 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <input value={newQk.price} onChange={(e) => setNewQk((v) => ({ ...v, price: e.target.value }))}
                placeholder="Price" type="number" step="0.01" data-testid="epos-new-qk-price"
                className="w-full sm:w-28 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <select value={newQk.register_id} onChange={(e) => setNewQk((v) => ({ ...v, register_id: e.target.value }))}
                data-testid="epos-new-qk-scope" className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Company-wide</option>
                {registers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button onClick={addQuickKey} data-testid="epos-add-qk-btn"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
